import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckSquare, Plus, Trash2, Calendar, Check, Pencil, Clock, AlertTriangle, Bell, Send, Settings2,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { WsPageTitle } from "./WorkspaceLayout";
import WorkspaceEditorModal from "../../components/admin/WorkspaceEditorModal";
import { SingleRichEditor } from "../../components/admin/RichEditor";
import { Field } from "../../components/admin/Fields";
import { DateTimePicker, formatDMY } from "../../components/ui/datetime-picker";
import CategoryManager, { CategoryBadge, useCategories, CategorySelect, CategorySidebar, matchCategoryFilter } from "../../components/workspace/CategoryManager";

const PRIORITY_META = {
  high: { label: "Yüksek", dot: "bg-red-500", pill: "bg-red-50 text-red-800 border-red-200" },
  normal: { label: "Normal", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-800 border-amber-200" },
  low: { label: "Düşük", dot: "bg-stone-300", pill: "bg-stone-50 text-stone-700 border-stone-200" },
};

const empty = () => ({
  id: null, text: "", description: "", priority: "normal",
  due_date: "", deadline_at: "",
  remind_mode: "none", remind_at: "", remind_interval_min: 60,
  category: "",
});

export default function Todos() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("open"); // open | done | all
  const [catFilter, setCatFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState(empty());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const { categories } = useCategories("todos");

  const doneParam = filter === "all" ? undefined : filter === "done" ? 1 : 0;
  const { data = [] } = useQuery({
    queryKey: ["workspace", "todos", filter],
    queryFn: () => api.get("/admin/workspace/todos", { params: doneParam === undefined ? {} : { done: doneParam } }).then((r) => r.data),
  });

  const items = data.filter((t) => matchCategoryFilter(t.category, catFilter, categories));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["workspace", "todos"] });
    qc.invalidateQueries({ queryKey: ["workspace", "overview"] });
  };

  const openNew = () => { setActive(empty()); setDirty(false); setErrors({}); setModalOpen(true); };
  const openEdit = (t) => {
    setActive({
      ...empty(),
      ...t,
      deadline_at: t.deadline_at || "",
      due_date: t.due_date || "",
      remind_mode: t.remind_mode || "none",
      remind_at: t.remind_at || "",
      remind_interval_min: t.remind_interval_min || 60,
      category: t.category || "",
    });
    setDirty(false); setErrors({}); setModalOpen(true);
  };

  const setField = (k, v) => {
    setActive((a) => ({ ...a, [k]: v }));
    setDirty(true);
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = (vals) => {
    const errs = {};
    if (!vals.text || !vals.text.trim()) errs.text = "Görev başlığı gerekli";
    if (vals.remind_mode !== "none" && !vals.remind_at) {
      errs.remind_at = "Hatırlatma modu seçildi — tarih/saat gerekli";
    }
    return errs;
  };

  const save = async () => {
    const errs = validate(active);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error(`Eksik alan var: ${Object.values(errs).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        text: active.text.trim(),
        priority: active.priority,
        due_date: active.due_date || null,
        description: active.description || "",
        deadline_at: active.deadline_at || null,
        remind_mode: active.remind_mode || "none",
        remind_at: active.remind_at || null,
        remind_interval_min: Number(active.remind_interval_min) || 60,
        category: active.category || "",
      };
      if (active.id) {
        await api.put(`/admin/workspace/todos/${active.id}`, payload);
        toast.success("Görev güncellendi");
      } else {
        await api.post("/admin/workspace/todos", payload);
        toast.success("Görev eklendi");
      }
      setDirty(false);
      setModalOpen(false);
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const toggle = async (id) => {
    try { await api.post(`/admin/workspace/todos/${id}/toggle`); invalidate(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Görev silinsin mi?")) return;
    try { await api.delete(`/admin/workspace/todos/${id}`); invalidate(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const isOverdue = (t) => {
    if (t.done) return false;
    const when = t.deadline_at || (t.due_date ? `${t.due_date}T23:59:59` : null);
    if (!when) return false;
    try { return new Date(when).getTime() < Date.now(); } catch { return false; }
  };

  return (
    <div data-testid="ws-todos">
      <WsPageTitle
        icon={CheckSquare}
        title="Yapılacaklar"
        sub="Küçük notlardan uzun hedeflere, her şey bir listede."
        action={
          <>
            <Button variant="outline" size="sm" onClick={() => setCatMgrOpen(true)} data-testid="ws-todo-cat-mgr">
              <Settings2 size={13} className="mr-1" /> Kategoriler
            </Button>
            <Button onClick={openNew} data-testid="ws-todo-new"><Plus size={14} className="mr-1" />Yeni görev</Button>
          </>
        }
      />

      {/* Sidebar + content */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4" data-testid="ws-todos-layout">
        <CategorySidebar
          kind="todos"
          items={data}
          value={catFilter}
          onChange={setCatFilter}
          onManage={() => setCatMgrOpen(true)}
          accent="amber"
          allLabel="Tüm Görevler"
        />

        <div className="min-w-0 space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-2 text-sm flex-wrap">
            {[["open", "Açık"], ["done", "Tamamlanmış"], ["all", "Hepsi"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-full border text-xs ${filter === v ? "bg-amber-50 border-amber-300 text-amber-800 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`} data-testid={`ws-todo-filter-${v}`}>
                {l}
              </button>
            ))}
          </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-amber-200 p-10 text-center text-stone-500">
          <CheckSquare size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Görev yok.</p>
          <Button onClick={openNew} className="mt-3" size="sm"><Plus size={13} className="mr-1" />İlk görevini oluştur</Button>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="ws-todos-list">
          {items.map((t) => {
            const pm = PRIORITY_META[t.priority] || PRIORITY_META.normal;
            const overdue = isOverdue(t);
            const catMeta = categories.find((c) => c.name === t.category);
            return (
              <li key={t.id}
                  className={`flex items-start gap-3 bg-white rounded-xl border p-3 shadow-sm transition
                    ${t.done ? "opacity-60 border-stone-200" : overdue ? "border-red-300 bg-red-50/40" : "border-stone-200"}`}
                  data-testid={`ws-todo-item-${t.id}`}>
                <button
                  onClick={() => toggle(t.id)}
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${t.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-stone-300 hover:border-emerald-400"}`}
                  data-testid={`ws-todo-toggle-${t.id}`}
                >
                  {t.done && <Check size={14} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${t.done ? "line-through text-stone-500" : "text-slate-900"}`}>{t.text}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 border ${pm.pill}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${pm.dot}`} />{pm.label}
                    </span>
                    {t.category && (
                      <CategoryBadge name={t.category} color={catMeta?.color} />
                    )}
                    {(t.deadline_at || t.due_date) && (
                      <span className={`text-[11px] inline-flex items-center gap-1 ${overdue ? "text-red-700 font-semibold" : "text-stone-600"}`}>
                        {overdue ? <AlertTriangle size={11} /> : <Clock size={11} />}
                        {t.deadline_at ? formatDMY(t.deadline_at, true) : t.due_date}
                      </span>
                    )}
                    {t.remind_mode && t.remind_mode !== "none" && t.remind_at && (
                      <span className="text-[11px] inline-flex items-center gap-1 text-indigo-700" title="Telegram hatırlatma">
                        <Send size={11} />
                        {formatDMY(t.remind_at, true)}
                        {t.remind_mode === "until_done" && " ↻ bitene dek"}
                        {t.remind_mode === "recurring" && ` ↻ ${t.remind_interval_min}dk`}
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <div
                      className="rich-content text-xs text-stone-600 mt-1 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: t.description }}
                    />
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`ws-todo-edit-${t.id}`}><Pencil size={11} /></Button>
                  <button onClick={() => remove(t.id)} className="text-stone-400 hover:text-red-600 p-2" data-testid={`ws-todo-del-${t.id}`}><Trash2 size={14} /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
        </div>
      </div>

      {/* Modal */}
      <WorkspaceEditorModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setDirty(false); }}
        title={active.id ? "Görevi düzenle" : "Yeni görev"}
        icon={CheckSquare}
        size="lg"
        busy={busy}
        dirty={dirty}
        onSubmit={save}
        submitLabel={active.id ? "Güncelle" : "Oluştur"}
        testid="ws-todo-modal"
        accent="amber"
      >
        <div className="space-y-3">
          <Field label="Görev başlığı *">
            <Input
              value={active.text}
              onChange={(e) => setField("text", e.target.value)}
              placeholder="Ne yapılacak?"
              className={`text-base font-semibold ${errors.text ? "border-red-400 focus:border-red-500 ring-red-100" : ""}`}
              data-testid="ws-todo-text"
              autoFocus
            />
            {errors.text && (
              <p className="text-xs text-red-600 mt-1" data-testid="ws-todo-text-error">{errors.text}</p>
            )}
          </Field>

          <Field label="Açıklama (opsiyonel)">
            <SingleRichEditor
              value={active.description}
              onChange={(v) => setField("description", v)}
              testid="ws-todo-description"
              minH="min-h-[180px]"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Öncelik">
              <select
                value={active.priority}
                onChange={(e) => setField("priority", e.target.value)}
                className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                data-testid="ws-todo-priority"
              >
                <option value="low">🟢 Düşük</option>
                <option value="normal">🟡 Normal</option>
                <option value="high">🔴 Yüksek</option>
              </select>
            </Field>

            <Field label="Son tarih (deadline)">
              <DateTimePicker
                value={active.deadline_at}
                onChange={(iso) => setField("deadline_at", iso)}
                testId="ws-todo-deadline"
              />
            </Field>
          </div>

          <Field label="Kategori">
            <CategorySelect
              value={active.category}
              onChange={(v) => setField("category", v)}
              kind="todos"
              testid="ws-todo-category"
              onManage={() => setCatMgrOpen(true)}
            />
          </Field>

          <div className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-3 space-y-3">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-900 uppercase tracking-wider">
                Telegram hatırlatma
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Mod">
                <select
                  value={active.remind_mode}
                  onChange={(e) => setField("remind_mode", e.target.value)}
                  className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                  data-testid="ws-todo-remind-mode"
                >
                  <option value="none">Kapalı</option>
                  <option value="once">Tek sefer</option>
                  <option value="until_done">Yapılana kadar tekrarla</option>
                  <option value="recurring">Düzenli aralıkla tekrarla</option>
                </select>
              </Field>
              {active.remind_mode !== "none" && (
                <Field label="İlk hatırlatma">
                  <DateTimePicker
                    value={active.remind_at}
                    onChange={(iso) => setField("remind_at", iso)}
                    testId="ws-todo-remind-at"
                    error={!!errors.remind_at}
                  />
                  {errors.remind_at && (
                    <p className="text-xs text-red-600 mt-1" data-testid="ws-todo-remind-at-error">
                      {errors.remind_at}
                    </p>
                  )}
                </Field>
              )}
            </div>
            {(active.remind_mode === "until_done" || active.remind_mode === "recurring") && (
              <Field label="Tekrar aralığı (dakika)">
                <Input
                  type="number"
                  min={5}
                  max={10080}
                  value={active.remind_interval_min}
                  onChange={(e) => setField("remind_interval_min", e.target.value)}
                  className="w-32"
                  data-testid="ws-todo-remind-interval"
                />
              </Field>
            )}
            <p className="text-[11px] text-stone-500 flex items-start gap-1">
              <Send size={11} className="shrink-0 mt-0.5" />
              Telegram botuyla bağlıysanız bu saatlerde özel sohbete bildirim atılır. <code className="bg-white border rounded px-1">/bitti &lt;no&gt;</code> ile tamamlayabilirsiniz.
            </p>
          </div>

          <p className="text-[11px] text-stone-500 flex items-center gap-1">
            <Calendar size={11} />
            Deadline belirlerseniz süresi geçtiğinde görev kırmızı ile vurgulanır. Tarih formatı: GG/AA/YYYY · Saat: 24h (SS:DD).
          </p>
        </div>
      </WorkspaceEditorModal>

      <CategoryManager
        open={catMgrOpen}
        onClose={() => setCatMgrOpen(false)}
        kind="todos"
        title="Görev kategorilerini yönet"
      />
    </div>
  );
}
